var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useRef, useState } from 'react';
import { Image, Pressable, View } from 'react-native';
import { BLACK_700, RED_500, tokens } from '../../constants';
import { StateConfigContext } from '../../providers';
import { getTestId } from '../../utils';
import { DynamicSvg } from '../dynamic-svg';
import { Flourish } from '../flourish';
import { InfoTag } from '../info-tag';
import { Lottie } from '../lottie/Lottie';
import { Notifier } from '../notifier';
import { Typography } from '../typography';
import { getIconButtonProps, getIconButtonStyles, getLabelTypes } from './IconButtonStyles';
export var IconButton = forwardRef((props, ref) => {
  var stateContext = useContext(StateConfigContext);
  var iconButtonProps = getIconButtonProps(props, stateContext);
  var stylesBasedOnState = useRef({});
  var size = iconButtonProps.size,
    iconName = iconButtonProps.iconName,
    iconProps = iconButtonProps.iconProps,
    label = iconButtonProps.label,
    controlState = iconButtonProps.controlState,
    _disabled = iconButtonProps.disabled,
    style = iconButtonProps.style,
    showFlourish = iconButtonProps.showFlourish,
    showNotifier = iconButtonProps.showNotifier,
    notifierProps = iconButtonProps.notifierProps,
    lottieProps = iconButtonProps.lottieProps,
    showInfoTag = iconButtonProps.showInfoTag,
    infoTagText = iconButtonProps.infoTagText,
    _iconButtonProps$test = iconButtonProps.testID,
    testID = _iconButtonProps$test === void 0 ? 'IconButton' : _iconButtonProps$test,
    variant = iconButtonProps.variant,
    imageUrl = iconButtonProps.imageUrl,
    onErrorImageUrl = iconButtonProps.onErrorImageUrl,
    imageProps = iconButtonProps.imageProps,
    _iconButtonProps$acce = iconButtonProps.accessibilityProps,
    accessibilityProps = _iconButtonProps$acce === void 0 ? {} : _iconButtonProps$acce,
    onLottieAnimationFailure = iconButtonProps.onLottieAnimationFailure,
    restProps = __rest(iconButtonProps, ["size", "iconName", "iconProps", "label", "controlState", "disabled", "style", "showFlourish", "showNotifier", "notifierProps", "lottieProps", "showInfoTag", "infoTagText", "testID", "variant", "imageUrl", "onErrorImageUrl", "imageProps", "accessibilityProps", "onLottieAnimationFailure"]);
  var disabled = _disabled || controlState === 'disabled';
  var _useState = useState(imageUrl),
    imgUrl = _useState[0],
    setImgUrl = _useState[1];
  var getPressableStyle = useCallback(event => {
    var styles = getIconButtonStyles(iconButtonProps, event);
    stylesBasedOnState.current = styles;
    return [styles.container, style];
  }, [iconButtonProps]);
  var COMPONENT = {
    icon: _jsx(_Fragment, {
      children: !!iconName && _jsx(DynamicSvg, Object.assign({
        iconName: iconName
      }, iconProps, getTestId(testID, 'icon')))
    }),
    image: _jsx(_Fragment, {
      children: !!imageUrl && _jsx(Image, Object.assign({
        resizeMode: "contain"
      }, imageProps, {
        source: {
          uri: imgUrl
        },
        style: [{
          width: '100%',
          height: '100%',
          borderRadius: tokens.borderRadius.BORDER_RADIUS_100,
          overflow: 'hidden'
        }, imageProps === null || imageProps === void 0 ? void 0 : imageProps.style]
      }, getTestId(testID, 'imageUrl'), {
        onError: () => {
          if (onErrorImageUrl) setImgUrl(onErrorImageUrl);
        }
      }))
    }),
    lottie: _jsx(Lottie, Object.assign({}, lottieProps, {
      onAnimationFailure: onLottieAnimationFailure
    }))
  };
  return _jsx(Pressable, Object.assign({
    ref: ref,
    style: getPressableStyle,
    disabled: disabled
  }, getTestId(testID), accessibilityProps, restProps, {
    children: () => _jsxs(View, Object.assign({
      style: stylesBasedOnState.current.iconPosition
    }, {
      children: [_jsx(Flourish, Object.assign({
        show: showFlourish,
        size: size,
        color: disabled ? BLACK_700 : RED_500,
        offset: {
          y: -4
        }
      }, getTestId(testID, 'showFlourish'), {
        children: _jsxs(View, Object.assign({
          style: stylesBasedOnState.current.iconContainer
        }, {
          children: [COMPONENT[variant], showNotifier && _jsx(Notifier, Object.assign({}, notifierProps, {
            style: stylesBasedOnState.current.notifier,
            size: (notifierProps === null || notifierProps === void 0 ? void 0 : notifierProps.hasDot) ? size == 'lg' ? 'md' : 'sm' : 'lg'
          }, getTestId(testID, 'showNotifier'))), showInfoTag && infoTagText && _jsx(InfoTag, Object.assign({
            size: size == 'lg' ? 'md' : 'sm',
            subType: "new",
            text: infoTagText,
            style: stylesBasedOnState.current.infoTag
          }, getTestId(testID, 'infoTagText')))]
        }))
      })), !!label && _jsx(Typography, Object.assign({
        text: label,
        type: getLabelTypes(size),
        style: stylesBasedOnState.current.iconButtonLabel,
        color: stylesBasedOnState.current.iconButtonLabel.color
      }, getTestId(testID, 'label')))]
    }))
  }));
});